import { useContext, type ReactElement, useState, useEffect } from 'react';
import { ClientSearchCard } from '../Components/ClientSearchCard';
import { ButtonCard } from '../Components/ButtonCard';
import { Legend } from '@/components/Calendar/Legend/Legend';
import { PlanningContainer } from '../Components/PlanningContainer';
import { openReportsModal } from '../Modals/ReportsModal';
import {
  type PlanningContextType,
  StateContext,
} from '@/layouts/Planning/PlanningLayout';
import { FormModal } from '@/components/Modal/FormModal';
import { useDisclosure } from '@mantine/hooks';
import PlanningActionHandler from '@/pages/Planning/Helpers/PlanningActionHandler';
import { getFetcher } from '@/api/fetchers';
import { displayError } from '@/utils/error/errorHandler';

export const PlannedPage = (): ReactElement => {
  const { planId, service, clientPlanId } =
    useContext<PlanningContextType>(StateContext);
  const [toForPlanningStateOpened, { toggle: toggleToForPlanningState }] =
    useDisclosure(false);
  const [toPlanningStateOpened, { toggle: toggleToPlanningState }] =
    useDisclosure(false);
  const [lockPlanOpened, { toggle: toggleLockPlan }] = useDisclosure(false);
  const [updateKey, setUpdateKey] = useState(0); // Add a state to force rerender
  const [isPlanLocked, setIsPlanLocked] = useState<boolean | undefined>(
    undefined
  );

  const {
    handleToForPlanningState,
    handleToPlanningState,
    noClientPlanSelected,
    lockPlan,
  } = PlanningActionHandler();

  useEffect(() => {
    const fetchPlanStatus = async () => {
      try {
        const response = await getFetcher(
          `/services/${service}/plans/${planId}`
        );
        setIsPlanLocked(response.locked);
      } catch (err) {
        displayError(err);
      }
    };

    if (service && planId) {
      fetchPlanStatus();
    }
  }, [service, planId, updateKey]);

  return (
    <PlanningContainer key={updateKey} planState="PLANNED">
      <ClientSearchCard planState="PLANNED" />
      <ButtonCard
        header="Přesunout plán do"
        buttons={[
          {
            label: 'Plánování',
            onClick: () =>
              clientPlanId
                ? toggleToPlanningState()
                : noClientPlanSelected(clientPlanId),
          },
          {
            label: 'K Doplánování',
            onClick: () =>
              clientPlanId
                ? toggleToForPlanningState()
                : noClientPlanSelected(clientPlanId),
          },
        ]}
      />
      <ButtonCard
        header="Hromadné akce"
        buttons={[
          {
            label: (
              <span>{isPlanLocked ? 'Odemknout plán' : `Uzamknout plán`}</span>
            ),
            onClick: () => toggleLockPlan(),
          },
          {
            label: 'PDF plány',
            onClick: () => openReportsModal(planId, service, 'PLAN'),
          },
        ]}
      />
      <FormModal
        opened={toForPlanningStateOpened}
        onClose={() => {
          toggleToForPlanningState();
        }}
        onConfirm={async event => {
          await handleToForPlanningState(
            event,
            clientPlanId,
            toggleToForPlanningState
          );
          setUpdateKey(prevKey => prevKey + 1); // Update the state to force rerender
        }}
        title={
          'Opravdu si přejete přesunout měsíční plán klienta do sekce K doplánování?'
        }
        confirmText={'Ano'}
      ></FormModal>
      <FormModal
        opened={toPlanningStateOpened}
        onClose={() => {
          toggleToPlanningState();
        }}
        onConfirm={async event => {
          await handleToPlanningState(
            event,
            clientPlanId,
            toggleToPlanningState
          );
          setUpdateKey(prevKey => prevKey + 1); // Update the state to force rerender
        }}
        title={
          'Opravdu si přejete přesunout měsíční plán klienta do sekce Plánování?'
        }
        confirmText={'Ano'}
      ></FormModal>
      <FormModal
        opened={lockPlanOpened}
        onClose={() => {
          toggleLockPlan();
        }}
        onConfirm={async event => {
          if (isPlanLocked !== undefined) {
            await lockPlan(
              event,
              service,
              planId,
              !isPlanLocked,
              toggleLockPlan
            );
            setUpdateKey(prevKey => prevKey + 1); // Update the state to force rerender
          }
        }}
        title={`Opravdu si přejete ${isPlanLocked ? 'odemknout' : 'uzamknout'} plán?`}
        confirmText={'Ano'}
      ></FormModal>
      <Legend
        label="Legenda"
        colors={[
          {
            label: 'Nepřiřazené',
            color: '#A0A0A0',
          },
          {
            label: 'Přiřazené',
            color: '#0000F0',
          },
        ]}
      />
    </PlanningContainer>
  );
};
